import { DateUtils } from "@/lib/utils/date";
import { Notification } from "@/models/notification";
import { defaultStoreCore as defaultStore } from "@/stores/default-store.core";
import { Format } from "./format";

import type {
   NotificationNameType,
   SerializedNotification,
} from "@laborchart-modules/lc-core-api/dist/api/notifications/get-user-notifications";
import type { useI18nContext } from "@procore/core-react";
import { formatName } from "./preferences";
import { formatDateTime } from "@procore/globalization-toolkit";

const notificationTranslationsPrefix = "views.company.workforce_planning.home.notifications.banner";

export const formatCreatedAt = (
   createdAt: number,
   I18n: ReturnType<typeof useI18nContext>,
): string =>
   formatDateTime(new Date(createdAt), {
      locale: I18n.locale,
      dateStyle: "short",
      timeStyle: "short",
   });

export const formatSubject = (
   notification: SerializedNotification,
   I18n: ReturnType<typeof useI18nContext>,
): string => {
   switch (notification.category) {
      case Notification.Category.PERSON_EXISTENCE_CHANGED:
         return notification.action === Notification.Action.CREATE
            ? I18n.t(`${notificationTranslationsPrefix}.subject.person_created`)
            : I18n.t(`${notificationTranslationsPrefix}.subject.person_deleted`);
      case Notification.Category.PERSON_GROUPS_CHANGED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.person_groups_changed`);
      case Notification.Category.PERSON_STATUS_CHANGED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.person_status_changed`);
      case Notification.Category.PERSON_TAG_WARNING:
         return I18n.t(`${notificationTranslationsPrefix}.subject.person_tag_warning`);
      case Notification.Category.PERSON_TAG_EXPIRED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.person_tag_expired`);
      case Notification.Category.PROJECT_EXISTENCE_CHANGED:
         return notification.action === Notification.Action.CREATE
            ? I18n.t(`${notificationTranslationsPrefix}.subject.project_created`)
            : I18n.t(`${notificationTranslationsPrefix}.subject.project_deleted`);
      case Notification.Category.PROJECT_GROUPS_CHANGED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.project_groups_changed`);
      case Notification.Category.PROJECT_STATUS_CHANGED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.project_status_changed`);
      case Notification.Category.ASSIGNED_AS_PROJECT_ROLE:
         return I18n.t(`${notificationTranslationsPrefix}.subject.selected_as_project_role`);
      case Notification.Category.ASSIGNMENT_CREATED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.new_assignment`);
      case Notification.Category.ASSIGNMENT_EDITED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.assignment_edited`);
      case Notification.Category.ASSIGNMENT_DELETED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.assignment_deleted`);
      case Notification.Category.ASSIGNMENT_STARTING:
         return I18n.t(`${notificationTranslationsPrefix}.subject.assignment_starting`);
      case Notification.Category.ASSIGNMENT_ENDING:
         return I18n.t(`${notificationTranslationsPrefix}.subject.assignment_ending`);
      case Notification.Category.REQUEST_CREATED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.new_request`);
      case Notification.Category.REQUEST_EDITED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.request_edited`);
      case Notification.Category.REQUEST_DELETED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.request_deleted`);
      case Notification.Category.REQUEST_FILLED:
         return I18n.t(`${notificationTranslationsPrefix}.subject.request_filled`);
      case Notification.Category.REQUEST_STARTING:
         return I18n.t(`${notificationTranslationsPrefix}.subject.request_starting`);
      case Notification.Category.REQUEST_ENDING:
         return I18n.t(`${notificationTranslationsPrefix}.subject.request_ending`);
      default:
         return "";
   }
};

export const isNotificationNameType = (name: unknown): name is NotificationNameType =>
   typeof name === "object" && name !== null && "first" in name && "last" in name;

const formatSubjectName = (name: string | NotificationNameType | null): string =>
   name
      ? isNotificationNameType(name)
         ? formatName({ first: name.first, last: name.last })
         : name
      : "";

export type NotificationContentWithList = {
   leadIn: string;
   listItems: string[];
};

export const formatContent = (
   notification: SerializedNotification,
   I18n: ReturnType<typeof useI18nContext>,
): string | NotificationContentWithList => {
   switch (notification.category) {
      // TODO: Handle Admin conversions here so it shows 'all' instead of '0'
      case Notification.Category.PERSON_EXISTENCE_CHANGED: {
         const { names: groupNames, count } = getGroupInfo(
            (notification?.details as any)?.groups,
            I18n,
         );
         const maybePluralGroup =
            count > 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.groups`)
               : I18n.t(`${notificationTranslationsPrefix}.content.group`);

         return notification.action === Notification.Action.CREATE
            ? I18n.t(`${notificationTranslationsPrefix}.content.person_existence_changed.created`, {
                 name: formatSubjectName(notification.subject_1_name),
                 group_names: groupNames,
                 maybe_plural_group: maybePluralGroup,
              })
            : I18n.t(`${notificationTranslationsPrefix}.content.person_existence_changed.deleted`, {
                 name: formatSubjectName(notification.subject_1_name),
                 group_names: groupNames,
                 maybe_plural_group: maybePluralGroup,
              });
      }
      case Notification.Category.PERSON_GROUPS_CHANGED: {
         const { names: groupNames } = getGroupInfo((notification?.details as any)?.groups, I18n);
         const name = formatSubjectName(notification.subject_1_name);

         return I18n.t(`${notificationTranslationsPrefix}.content.person_groups_changed.content`, {
            name,
            group_names: groupNames,
         });
      }
      case Notification.Category.PERSON_STATUS_CHANGED: {
         return I18n.t(`${notificationTranslationsPrefix}.content.person_status_changed.content`, {
            name: formatSubjectName(notification.subject_1_name),
            status: Format.capitalize((notification.meta as any).new_val),
         });
      }
      case Notification.Category.PERSON_TAG_WARNING: {
         const expirationDate = DateUtils.formatDetachedDay(
            (notification.meta as any).expr_date,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.person_tag_warning.content`, {
            tag_name: notification.subject_2_name,
            expr_days_warning: (notification.details as any).expr_days_warning,
            expire_date: expirationDate,
            name: formatSubjectName(notification.subject_1_name),
         });
      }
      case Notification.Category.PERSON_TAG_EXPIRED: {
         return I18n.t(`${notificationTranslationsPrefix}.content.person_tag_expired.content`, {
            tag_name: notification.subject_2_name,
            name: formatSubjectName(notification.subject_1_name),
         });
      }
      case Notification.Category.PROJECT_EXISTENCE_CHANGED: {
         const { names: groupNames, count } = getGroupInfo(
            (notification?.details as any)?.groups,
            I18n,
         );
         const maybePluralGroup =
            count > 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.groups`)
               : I18n.t(`${notificationTranslationsPrefix}.content.group`);

         return notification.action === Notification.Action.CREATE
            ? I18n.t(
                 `${notificationTranslationsPrefix}.content.project_existence_changed.created`,
                 {
                    name: notification.subject_1_name,
                    group_names: groupNames,
                    maybe_plural_group: maybePluralGroup,
                 },
              )
            : I18n.t(
                 `${notificationTranslationsPrefix}.content.project_existence_changed.deleted`,
                 {
                    name: notification.subject_1_name,
                    group_names: groupNames,
                    maybe_plural_group: maybePluralGroup,
                 },
              );
      }
      case Notification.Category.PROJECT_GROUPS_CHANGED: {
         const { names: groupNames } = getGroupInfo((notification?.details as any)?.groups, I18n);

         return I18n.t(`${notificationTranslationsPrefix}.content.project_groups_changed.content`, {
            name: notification.subject_1_name,
            group_names: groupNames,
         });
      }
      case Notification.Category.PROJECT_STATUS_CHANGED: {
         return I18n.t(`${notificationTranslationsPrefix}.content.project_status_changed.content`, {
            name: notification.subject_1_name,
            status: Format.capitalize((notification.meta as any).new_val),
         });
      }
      case Notification.Category.ASSIGNED_AS_PROJECT_ROLE: {
         const projectName = getProjectName(notification);

         return I18n.t(
            `${notificationTranslationsPrefix}.content.assigned_as_project_role.content`,
            {
               role_name: notification.subject_2_name,
               project_name: projectName,
            },
         );
      }
      case Notification.Category.ASSIGNMENT_CREATED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.assignment_created.content`, {
            name: formatSubjectName(notification.subject_2_name),
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.ASSIGNMENT_EDITED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.assignment_edited.content`, {
            name: formatSubjectName(notification.subject_2_name),
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.ASSIGNMENT_DELETED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = (notification.meta as any).end_day
            ? DateUtils.formatDetachedDay(
                 (notification.meta as any).end_day,
                 defaultStore.getDateFormat(),
              )
            : I18n.t(`${notificationTranslationsPrefix}.content.tbd`);

         return I18n.t(`${notificationTranslationsPrefix}.content.assignment_deleted.content`, {
            name: formatSubjectName(notification.subject_2_name),
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.ASSIGNMENT_STARTING: {
         const assignments = Array.isArray((notification?.meta as any)?.assignments)
            ? (notification?.meta as any)?.assignments
            : [];
         const projectName = getProjectName(notification);
         const leadIn =
            assignments.length === 0
               ? I18n.t(`${notificationTranslationsPrefix}.content.assignment_starting.zero`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 })
               : assignments.length === 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.assignment_starting.one`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 })
               : I18n.t(`${notificationTranslationsPrefix}.content.assignment_starting.other`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 });
         const listItems = [];
         for (const item of assignments) {
            const startDate = DateUtils.formatDetachedDay(
               item.start_day,
               defaultStore.getDateFormat(),
            );
            const endDate = DateUtils.formatDetachedDay(item.end_day, defaultStore.getDateFormat());
            listItems.push(
               I18n.t(
                  `${notificationTranslationsPrefix}.content.assignment_starting.assignment_item`,
                  {
                     name: formatName({
                        first: item.person_name.first,
                        last: item.person_name.last,
                     }),
                     start_date: startDate,
                     end_date: endDate,
                  },
               ),
            );
         }

         return { leadIn, listItems };
      }
      case Notification.Category.ASSIGNMENT_ENDING: {
         const assignments = Array.isArray((notification?.meta as any)?.assignments)
            ? (notification?.meta as any)?.assignments
            : [];
         const projectName = getProjectName(notification);

         const leadIn =
            assignments.length === 0
               ? I18n.t(`${notificationTranslationsPrefix}.content.assignment_ending.zero`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 })
               : assignments.length === 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.assignment_ending.one`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 })
               : I18n.t(`${notificationTranslationsPrefix}.content.assignment_ending.other`, {
                    assignments_length: assignments.length,
                    project_name: projectName,
                 });
         const listItems = [];
         for (const item of assignments) {
            const startDate = DateUtils.formatDetachedDay(
               item.start_day,
               defaultStore.getDateFormat(),
            );
            const endDate = DateUtils.formatDetachedDay(item.end_day, defaultStore.getDateFormat());
            listItems.push(
               I18n.t(
                  `${notificationTranslationsPrefix}.content.assignment_ending.assignment_item`,
                  {
                     name: formatName({
                        first: item.person_name.first,
                        last: item.person_name.last,
                     }),
                     start_date: startDate,
                     end_date: endDate,
                  },
               ),
            );
         }

         return { leadIn, listItems };
      }
      case Notification.Category.REQUEST_CREATED: {
         const requestsCreated = (notification.meta as any).requests_created;
         const positionName = (notification.details as any)?.position?.name || "";
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return requestsCreated === 0
            ? I18n.t(`${notificationTranslationsPrefix}.content.request_created.zero`, {
                 requests_created: requestsCreated,
                 position_name: positionName,
                 project_name: projectName,
                 start_date: startDate,
                 end_date: endDate,
              })
            : requestsCreated === 1
            ? I18n.t(`${notificationTranslationsPrefix}.content.request_created.one`, {
                 requests_created: requestsCreated,
                 position_name: positionName,
                 project_name: projectName,
                 start_date: startDate,
                 end_date: endDate,
              })
            : I18n.t(`${notificationTranslationsPrefix}.content.request_created.other`, {
                 requests_created: requestsCreated,
                 position_name: positionName,
                 project_name: projectName,
                 start_date: startDate,
                 end_date: endDate,
              });
      }
      case Notification.Category.REQUEST_EDITED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.request_edited.content`, {
            position_name: (notification.details as any)?.position?.name || "",
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.REQUEST_DELETED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.request_deleted.content`, {
            position_name: (notification.details as any)?.position?.name || "",
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.REQUEST_FILLED: {
         const projectName = getProjectName(notification);
         const startDate = DateUtils.formatDetachedDay(
            (notification.meta as any).start_day,
            defaultStore.getDateFormat(),
         );
         const endDate = DateUtils.formatDetachedDay(
            (notification.meta as any).end_day,
            defaultStore.getDateFormat(),
         );

         return I18n.t(`${notificationTranslationsPrefix}.content.request_filled.content`, {
            name: formatSubjectName(notification.subject_3_name),
            position_name: (notification.details as any)?.position?.name || "",
            project_name: projectName,
            start_date: startDate,
            end_date: endDate,
         });
      }
      case Notification.Category.REQUEST_STARTING: {
         const requests = Array.isArray((notification?.meta as any)?.requests)
            ? (notification?.meta as any)?.requests
            : [];
         const projectName = getProjectName(notification);
         const leadIn =
            requests.length === 0
               ? I18n.t(`${notificationTranslationsPrefix}.content.request_starting.zero`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 })
               : requests.length === 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.request_starting.one`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 })
               : I18n.t(`${notificationTranslationsPrefix}.content.request_starting.other`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 });
         const listItems = [];
         for (const item of requests) {
            const formattedStartDate = DateUtils.formatDetachedDay(
               item.start_day,
               defaultStore.getDateFormat(),
            );
            const formattedEndDate = DateUtils.formatDetachedDay(
               item.end_day,
               defaultStore.getDateFormat(),
            );
            listItems.push(
               I18n.t(`${notificationTranslationsPrefix}.content.request_starting.request_item`, {
                  position_name:
                     item.position_name ||
                     I18n.t(`${notificationTranslationsPrefix}.content.resource_request`),
                  start_date: formattedStartDate,
                  end_date: formattedEndDate,
               }),
            );
         }

         return { leadIn, listItems };
      }
      case Notification.Category.REQUEST_ENDING: {
         const requests = Array.isArray((notification?.meta as any)?.requests)
            ? (notification?.meta as any)?.requests
            : [];
         const projectName = getProjectName(notification);
         const leadIn =
            requests.length === 0
               ? I18n.t(`${notificationTranslationsPrefix}.content.request_ending.zero`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 })
               : requests.length === 1
               ? I18n.t(`${notificationTranslationsPrefix}.content.request_ending.one`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 })
               : I18n.t(`${notificationTranslationsPrefix}.content.request_ending.other`, {
                    requests_length: requests.length,
                    project_name: projectName,
                 });
         const listItems = [];
         for (const item of requests) {
            const startDate = DateUtils.formatDetachedDay(
               item.start_day,
               defaultStore.getDateFormat(),
            );
            const endDate = DateUtils.formatDetachedDay(item.end_day, defaultStore.getDateFormat());
            listItems.push(
               I18n.t(`${notificationTranslationsPrefix}.content.request_ending.request_item`, {
                  position_name:
                     item.position_name ||
                     I18n.t(`${notificationTranslationsPrefix}.content.resource_request`),
                  start_date: startDate,
                  end_date: endDate,
               }),
            );
         }

         return { leadIn, listItems };
      }
      default:
         return "";
   }
};

// exported for testing
export const getGroupInfo = (
   notificationGroups: any[],
   I18n: ReturnType<typeof useI18nContext>,
): { count: number; names: string } => {
   const groupInfo = { count: 0, names: "" };
   const groups = Array.isArray(notificationGroups)
      ? notificationGroups
           .filter((group: any) => !group.archived)
           .map((group: any) => group.name)
           .sort()
      : [];
   groupInfo.count = groups.length;
   let groupNames = groups.slice(0, 5).join(", ");
   if (groups.length > 5) {
      groupNames += `, ${I18n.t(`${notificationTranslationsPrefix}.content.and_n_more_groups`, {
         quantity: groups.length - 5,
      })}`;
   }
   groupInfo.names = groupNames;
   return groupInfo;
};

// exported for testing
export const getProjectName = (notification: SerializedNotification) => {
   return notification.baggage?.job_number
      ? `${notification.subject_1_name} - ${notification.baggage.job_number}`
      : notification.subject_1_name;
};
